// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import BEST_FEMALE_PIN_IMAGES from 'app/common/react/components/growth/activation/NuxConst/bestFemalePinImages';
import BEST_GENDER_AGNOSTIC_PIN_IMAGES from 'app/common/react/components/growth/activation/NuxConst/bestGenderAgnosticPinImages';
import BEST_MALE_PIN_IMAGES from 'app/common/react/components/growth/activation/NuxConst/bestMalePinImages';
import type { Dispatch } from 'redux';
import type { NuxStep } from 'app/common/redux/actions/nuxProgress';
import type { State as ReduxState } from 'app/common/redux/types';
import UnauthUsage from 'app/common/lib/UnauthUsage';
import { BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { compose } from 'redux';
import {
  type ImageDimensionsInfo,
  getImageSignatureFromURL,
} from 'app/common/react/components/growth/activation/NuxUtils/NuxUtils';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Image, Mask, Masonry } from 'gestalt';
import { getGridItemGutter, MIN_COLUMNS } from 'app/common/lib/react/gridUtils';
import { SingleTreatmentExp, MultiTreatmentExp } from 'app/common/lib/experiment';

type ReduxStateProps = {|
  nuxSelectedInterestImages?: ?Array<ImageDimensionsInfo>,
  nuxStep?: ?NuxStep,
|};

type ReduxDispatchProps = {|
  dispatch: Dispatch<any>,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type OwnProps = {|
  disableFlexibleBGPins: boolean,
  isInNux?: boolean,
  isInRenux?: boolean,
  pinImages?: Array<ImageDimensionsInfo>,
  gender?: ?string,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...HOCProps,
|};

type State = {|
  loading: boolean,
  lpPinImages: Array<ImageDimensionsInfo>,
  seoPinInserted: boolean,
  shouldLoadProgressively: boolean,
|};

class BackgroundPinImages extends PureComponent<Props, State> {
  state = {
    loading: true,
    lpPinImages: [],
    seoPinInserted: false,
    shouldLoadProgressively: false,
  };

  // Used for setting number of Pin images, based on indices defined in the const, to show based on NUX step
  progressiveBackgroundImageIndices: Array<number> = BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP.welcome;
  // Used for setting topic images as background images, gets set when user is on topic picker and selected interest(s)
  bgPinsWithSelectedTopicImages: Array<ImageDimensionsInfo> = [];
  // Store a unique set of parsed URLS  for any added unauth bg images, to prevent duplicates from appearing in the bg
  // when a topic is selected in topic picker. Default bg images assumed not to be duplicated in the picker
  lpPinImagesParsedURLs: Set<string> = new Set();
  // Used for the best pin images background preview experiment, gets set when user selects a topic in topic picker
  bestPinsPreviewImages: Array<Object> = [];
  //Used for the best pin images background preview experiment, stores best pins image objects
  selectedTopicBestPins: ?Array<Object> = [];

  componentDidMount() {
    const { isInNux, isInRenux, pinImages = [], requestContext, gender } = this.props;

    const unauthPinRelatedImages = this.getUnauthPinImages() || [];
    const unauthLastRelatedPinImage = unauthPinRelatedImages[0];

    const unauthVisitedPinImages = this.getUnauthPinImages(true) || [];
    const unauthLastVisitedPinImage = unauthVisitedPinImages[0];

    let lpPinImages = [...pinImages];
    let seoPinAddedToBg = false;

    if (unauthLastRelatedPinImage) {
      let insertVisitedPin = false;

      const visitedPinDifferentFromRelated =
        unauthLastVisitedPinImage &&
        (unauthLastRelatedPinImage || {}).url !== (unauthLastVisitedPinImage || {}).url;

      if (visitedPinDifferentFromRelated) {
        if (isInNux) {
          const webSessionBGPinsExp = new SingleTreatmentExp(requestContext, 'web_session_bg_pins');
          if (webSessionBGPinsExp.enabled()) {
            insertVisitedPin = true;
          }
        } else if (isInRenux) {
          const webSessionBGPinsRenuxExp = new SingleTreatmentExp(
            requestContext,
            'web_session_bg_pins_renux',
          );
          if (webSessionBGPinsRenuxExp.enabled()) {
            insertVisitedPin = true;
          }
        }
      }

      if (insertVisitedPin) {
        lpPinImages = [unauthLastVisitedPinImage, ...lpPinImages];
      } else {
        lpPinImages = [unauthLastRelatedPinImage, ...lpPinImages];
      }

      seoPinAddedToBg = true;
    }

    const progressiveNuxBackgroundPinsExp = new SingleTreatmentExp(
      requestContext,
      'web_nux_progressive_bg_pins',
    );
    const inProgressiveNuxBackgroundPinsExp =
      (isInRenux || isInNux) && progressiveNuxBackgroundPinsExp.enabled();

    const newState = {
      loading: false,
      lpPinImages,
      shouldLoadProgressively: inProgressiveNuxBackgroundPinsExp,
    };

    // If props `pinImages` didn't supply enough images, add static gender agnostic pin images
    if (newState.lpPinImages.length < 15) {
      let extraPinImages;
      if (gender && gender !== 'unspecified') {
        const webNuxGenderBackgroundPinsExp = new SingleTreatmentExp(
          requestContext,
          'web_nux_gender_bg_pins',
        );
        requestContext.experiments.flush();

        if (webNuxGenderBackgroundPinsExp.enabled()) {
          extraPinImages = gender === 'male' ? BEST_MALE_PIN_IMAGES : BEST_FEMALE_PIN_IMAGES;
        }
      }

      if (!extraPinImages) {
        extraPinImages = BEST_GENDER_AGNOSTIC_PIN_IMAGES;
      }

      newState.lpPinImages = newState.lpPinImages.concat(extraPinImages);
    }

    setTimeout(() => this.setState(newState));

    if (seoPinAddedToBg) {
      lpPinImages.forEach((lpPinImage) =>
        this.lpPinImagesParsedURLs.add(getImageSignatureFromURL(lpPinImage)),
      );
      this.setState({ seoPinInserted: true });
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const { isInNux, isInRenux, nuxSelectedInterestImages, nuxStep, requestContext } = nextProps;
    const { lpPinImages, seoPinInserted, shouldLoadProgressively } = this.state;
    const prevNuxSelectedInterestImages = this.props.nuxSelectedInterestImages;

    // Update image indices if background images should load progressively
    if (nuxStep && shouldLoadProgressively) {
      this.progressiveBackgroundImageIndices = BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP[nuxStep];
    }

    const topicFlipExp = new MultiTreatmentExp(requestContext, 'web_nux_picker_flip');
    const showAnimatedBackground = !topicFlipExp.checkWithNoActivation('enabled_flip_no_bg');

    //TODO ychaiuzhang: refactor file since lpPinImages is altered in so many diff areas of code
    // If user is on topic picker step in NUX, update selected topics' images
    if (
      (isInNux || isInRenux) &&
      nuxStep === 'topic' &&
      showAnimatedBackground &&
      prevNuxSelectedInterestImages &&
      nuxSelectedInterestImages &&
      prevNuxSelectedInterestImages.length !== nuxSelectedInterestImages.length
    ) {
      // Default selected topic images to a copy of the current state of background pins. We need to reset the list
      // everytime nuxSelectedInterestImages is updated since we revert background images back to the original list
      // when user unselects topics. Get image signatures to check for duplicates in selected topics.
      this.bgPinsWithSelectedTopicImages = Array.from(lpPinImages);
      if (nuxSelectedInterestImages && nuxSelectedInterestImages.length > 0) {
        let bgPinsIndex = 0;
        // if seo pin is inserted in the bg, preserve it and replace bg with selected
        // images after position 0
        if (seoPinInserted) {
          bgPinsIndex = 1;
        }
        for (const selectedImage of nuxSelectedInterestImages) {
          //check if selected topic is already shown as an unauth background pin by
          //comparing image signatures
          const selectedTopicParsedURL = getImageSignatureFromURL(selectedImage);
          if (!this.lpPinImagesParsedURLs.has(selectedTopicParsedURL)) {
            this.bgPinsWithSelectedTopicImages[bgPinsIndex] = selectedImage;
            bgPinsIndex += 1;
          }
        }
      }
    }

    const eligibleForBestPinsRenuxExp =
      isInRenux &&
      ['topic', 'loader'].includes(nuxStep) &&
      showAnimatedBackground &&
      ((prevNuxSelectedInterestImages && prevNuxSelectedInterestImages.length > 0) ||
        (nuxSelectedInterestImages && nuxSelectedInterestImages.length > 0));
    const noInterestsSelected =
      !nuxSelectedInterestImages || nuxSelectedInterestImages.length === 0;
    const interestsDeselected =
      prevNuxSelectedInterestImages &&
      nuxSelectedInterestImages &&
      nuxSelectedInterestImages.length < prevNuxSelectedInterestImages.length;
    const moreInterestsSelected = prevNuxSelectedInterestImages !== nuxSelectedInterestImages;

    if (eligibleForBestPinsRenuxExp || isInNux) {
      if (noInterestsSelected || interestsDeselected) {
        // set back to empty so that render will show default bg images instead
        this.bestPinsPreviewImages = [];
      } else if (moreInterestsSelected) {
        // since no best pins will be returned if user is in control, we need to activate on select instead
        // there is a chance that a user is in the enabled group and not see best pins, but we minimize that
        // by adding a group filter to locales/countries where we know are very likely to have best pins
        const inBestPinsRenuxExp = new SingleTreatmentExp(
          requestContext,
          'web_renux_best_pins_preview',
        );
        if (isInNux || inBestPinsRenuxExp.enabled()) {
          this.selectedTopicBestPins =
            nuxSelectedInterestImages &&
            nuxSelectedInterestImages[nuxSelectedInterestImages.length - 1].bestPinsImages;
          if (this.selectedTopicBestPins && this.selectedTopicBestPins.length > 0) {
            this.bestPinsPreviewImages =
              // only insert background pins from position 1 and on to keep any inserted PLP image
              seoPinInserted
                ? [lpPinImages[0], ...this.selectedTopicBestPins, ...lpPinImages.slice(1)]
                : [...this.selectedTopicBestPins, ...lpPinImages];
          }
        }
      }
    }
  }

  shouldShowBackgroundWithSelectedTopicImages = (): boolean =>
    // Only show selected topic images background if we're in topic picker or loader step. bgPinsWithSelectedTopicImages
    // is only populated if we are in the topic picker bg pins experiment
    !!this.bgPinsWithSelectedTopicImages &&
    this.bgPinsWithSelectedTopicImages.length > 0 &&
    (!!this.props.isInNux || !!this.props.isInRenux) &&
    ['topic', 'loader'].includes(this.props.nuxStep);

  shouldShowBestPinsPreview = (): boolean =>
    !!this.bestPinsPreviewImages && this.bestPinsPreviewImages.length > 0;

  getUnauthPinImages = (sessionOnly: boolean = false): Array<ImageDimensionsInfo> => {
    const pins = sessionOnly ? UnauthUsage.getUnauthSessionsPins() : UnauthUsage.getUnauthPins();
    // Filter out Pin objects without valid image field, and return list of Pin images. also remove duplicate image urls
    const pinImageUrls = new Set();
    return pins
      .filter((pin) => pin.images && pin.images['236x'])
      .map((pin) => pin.images['236x'])
      .filter((pinImage) => {
        const alreadyExistingUrl = pinImageUrls.has(pinImage.url);
        pinImageUrls.add(pinImage.url);
        return !alreadyExistingUrl;
      });
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderPinImage = (obj: *) => {
    const { data: image } = obj;
    const { height, url, width } = image;

    return (
      <Mask shape="rounded" wash>
        <Image alt="pin-image" src={url} naturalWidth={width} naturalHeight={height} />
      </Mask>
    );
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderPinImageProgressively = (obj: *) => {
    const { data: image, itemIdx } = obj;
    const { height, url, width } = image;
    const isShownImage = this.progressiveBackgroundImageIndices.indexOf(itemIdx) >= 0;
    const backgroundPinImage = (
      <Mask shape="rounded" wash>
        <Image alt="pin-image" src={url} naturalWidth={width} naturalHeight={height}>
          {!isShownImage && (
            <Box
              dangerouslySetInlineStyle={{
                __style: {
                  backgroundColor: 'rgba(2, 4, 4, 0.6)',
                  paddingBottom: `${(height / width) * 100}%`,
                },
              }}
            />
          )}
        </Image>
      </Mask>
    );

    return backgroundPinImage;
  };

  render() {
    const { disableFlexibleBGPins } = this.props;
    const { loading, lpPinImages, shouldLoadProgressively } = this.state;
    const defaultBgPins = this.shouldShowBackgroundWithSelectedTopicImages()
      ? this.bgPinsWithSelectedTopicImages
      : lpPinImages;

    return (
      !loading &&
      lpPinImages && (
        <Box
          bottom
          left
          maxHeight={2500}
          overflow="hidden"
          paddingY={6}
          paddingX={5}
          position="absolute"
          right
          top
          dangerouslySetInlineStyle={{
            __style: {
              pointerEvents: 'none',
            },
          }}
        >
          <Masonry
            flexible={!disableFlexibleBGPins}
            comp={shouldLoadProgressively ? this.renderPinImageProgressively : this.renderPinImage}
            gutterWidth={getGridItemGutter(true)}
            items={this.shouldShowBestPinsPreview() ? this.bestPinsPreviewImages : defaultBgPins}
            minCols={MIN_COLUMNS}
          />
        </Box>
      )
    );
  }
}

const mapStateToProps = (state: ReduxState): ReduxStateProps => ({
  nuxSelectedInterestImages:
    state.ui.nuxSelectedInterestImages && state.ui.nuxSelectedInterestImages.images,
  nuxStep: state.ui.nuxProgress && state.ui.nuxProgress.step,
});

export default compose(
  connect<_, OwnProps, ReduxStateProps, ReduxDispatchProps, _, _>(
    mapStateToProps,
    null,
  ),
  withRequestContext,
)(BackgroundPinImages);
